import { useState } from 'react';
import { X, AlertTriangle, ChevronDown, ChevronUp, Copy, Check } from 'lucide-react';

interface ErrorDetail {
  resource: string;
  friendlyMessage: string;
  technicalMessage: string;
  errorType: 'WORKER_LIMIT' | 'IP_PRIVATE' | 'BOOT_ERROR' | 'RLS_POLICY' | 'UNKNOWN';
  suggestion: string;
  downloadUrl?: string;
}

interface ErrorModalProps {
  isOpen: boolean;
  onClose: () => void;
  errors: string[];
  successCount: number;
  totalCount: number;
}

export function ErrorModal({ isOpen, onClose, errors, successCount, totalCount }: ErrorModalProps) {
  const [expandedErrors, setExpandedErrors] = useState<Set<number>>(new Set());
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  if (!isOpen) return null;

  // Parsear errores y categorizarlos
  const parsedErrors: ErrorDetail[] = errors.map(errorString => {
    const resourceMatch = errorString.match(/^(CSV|Excel|JSON|XML|Shapefile)\s+([^:]+):/);
    const resource = resourceMatch ? resourceMatch[2] : 'Recurso desconocido';

    let errorType: ErrorDetail['errorType'] = 'UNKNOWN';
    let friendlyMessage = 'Error desconocido al procesar el recurso';
    let suggestion = 'Contacte al administrador con los detalles técnicos.';
    let downloadUrl: string | undefined = undefined;

    if (errorString.includes('row-level security policy') || errorString.includes('RLS Policy')) {
      errorType = 'RLS_POLICY';
      friendlyMessage = 'Error de permisos en la base de datos';
      suggestion = 'Este archivo contiene datos con formato inusual que no pueden ser procesados debido a restricciones de seguridad. Descargue el archivo para revisarlo o contacte al administrador.';

      // Extract download URL if present
      const urlMatch = errorString.match(/(https?:\/\/[^\s\n]+)/);
      if (urlMatch) {
        downloadUrl = urlMatch[1];
      }
    } else if (errorString.includes('WORKER_LIMIT') || errorString.includes('not having enough compute resources')) {
      errorType = 'WORKER_LIMIT';
      friendlyMessage = 'El archivo es demasiado grande para procesarse';
      suggestion = 'Este recurso excede los límites de procesamiento. Considere dividirlo en archivos más pequeños o contacte al proveedor de CKAN.';
    } else if (errorString.includes('172.17.10.14') || errorString.includes('Connection timed out')) {
      errorType = 'IP_PRIVATE';
      friendlyMessage = 'El servidor CKAN tiene una configuración incorrecta';
      suggestion = 'El CKAN está devolviendo URLs internas (172.17.10.14) que no son accesibles desde internet. El administrador del CKAN debe corregir la configuración de ckan.site_url.';
    } else if (errorString.includes('BOOT_ERROR') || errorString.includes('failed to start')) {
      errorType = 'BOOT_ERROR';
      friendlyMessage = 'Error temporal del servidor';
      suggestion = 'Intente sincronizar este recurso nuevamente en unos minutos.';
    }

    return {
      resource,
      friendlyMessage,
      technicalMessage: errorString,
      errorType,
      suggestion,
      downloadUrl,
    };
  });

  const toggleExpand = (index: number) => {
    const newExpanded = new Set(expandedErrors);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedErrors(newExpanded);
  };

  const copyToClipboard = async (text: string, index: number) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const errorTypeColors = {
    WORKER_LIMIT: 'bg-orange-50 border-orange-200 text-orange-800',
    IP_PRIVATE: 'bg-red-50 border-red-200 text-red-800',
    BOOT_ERROR: 'bg-yellow-50 border-yellow-200 text-yellow-800',
    RLS_POLICY: 'bg-purple-50 border-purple-200 text-purple-800',
    UNKNOWN: 'bg-gray-50 border-gray-200 text-gray-800',
  };

  const errorTypeIcons = {
    WORKER_LIMIT: '⚠️',
    IP_PRIVATE: '🔒',
    BOOT_ERROR: '🔄',
    RLS_POLICY: '🛡️',
    UNKNOWN: '❓',
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-red-600 to-orange-600 text-white p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-8 h-8" />
            <div>
              <h2 className="text-2xl font-bold">Reporte de Sincronización</h2>
              <p className="text-red-100 text-sm mt-1">
                {successCount} exitosos, {errors.length} con errores de {totalCount} recursos
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-red-700 rounded-lg transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-4">
            {parsedErrors.map((error, index) => (
              <div
                key={index}
                className={`border rounded-lg overflow-hidden ${errorTypeColors[error.errorType]}`}
              >
                {/* Error Summary */}
                <div className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-2xl">{errorTypeIcons[error.errorType]}</span>
                        <h3 className="font-semibold text-lg">{error.resource}</h3>
                      </div>
                      <p className="font-medium mb-2">{error.friendlyMessage}</p>
                      <p className="text-sm opacity-90">{error.suggestion}</p>

                      {/* Download link for RLS errors */}
                      {error.downloadUrl && (
                        <div className="mt-3">
                          <a
                            href={error.downloadUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium text-sm transition-colors"
                          >
                            📥 Descargar archivo CSV
                          </a>
                        </div>
                      )}
                    </div>

                    {/* Expand/Collapse Button */}
                    <button
                      onClick={() => toggleExpand(index)}
                      className="p-2 hover:bg-black hover:bg-opacity-10 rounded-lg transition-colors flex items-center gap-1 text-sm font-medium"
                    >
                      {expandedErrors.has(index) ? (
                        <>
                          <ChevronUp className="w-4 h-4" />
                          Ocultar
                        </>
                      ) : (
                        <>
                          <ChevronDown className="w-4 h-4" />
                          Detalles
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* Technical Details (Collapsible) */}
                {expandedErrors.has(index) && (
                  <div className="border-t border-current border-opacity-20 bg-black bg-opacity-5 p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-semibold uppercase opacity-70">
                        Detalles Técnicos
                      </h4>
                      <button
                        onClick={() => copyToClipboard(error.technicalMessage, index)}
                        className="flex items-center gap-2 px-3 py-1 bg-white bg-opacity-50 hover:bg-opacity-70 rounded text-sm font-medium transition-colors"
                      >
                        {copiedIndex === index ? (
                          <>
                            <Check className="w-4 h-4" />
                            Copiado
                          </>
                        ) : (
                          <>
                            <Copy className="w-4 h-4" />
                            Copiar
                          </>
                        )}
                      </button>
                    </div>
                    <pre className="text-xs font-mono bg-black bg-opacity-10 p-3 rounded overflow-x-auto whitespace-pre-wrap break-words">
                      {error.technicalMessage}
                    </pre>
                    <p className="text-xs mt-2 opacity-70">
                      💡 Comparte estos detalles con el desarrollador o administrador del sistema CKAN.
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t p-4 bg-gray-50 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-medium transition-colors"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}
